import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ArrowLeft,
  Bell,
  MessageSquareMore,
  Pause,
  Phone,
  Play,
  Plus,
  RefreshCw,
  Search as SearchIcon,
  User,
  UserPlus,
  Voicemail
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import LogoutConfirmDialog from '@/components/common/LogoutConfirmDialog';
import NotificationBell from '@/components/common/NotificationBell';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { GlobalTimelog } from './GlobalTimelog';
import { APP_VERSION } from '@/config/envConfig';
import { useSMSCount } from '@/hooks/sms/useSMSCount';
import { useVoicemailCount } from '@/hooks/voicemail/useVoicemailCount';
import { useHBoxCall } from '@/components/CallHandler/hooks/useHBoxCall';
import { clinicsGraphqlService } from '@/services/api/clinics/clinicsGraphqlService';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface UnifiedHeaderProps {
  title: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
  showTimer?: boolean;
  sessionTime?: number;
  isPaused?: boolean;
  onToggleTimer?: () => void;
  showQuickActions?: boolean;
  onFollowUpClick?: () => void;
  onDeferredClick?: () => void;
  onSearchClick?: () => void;
  userType?: string;
  showFollowUp?: boolean;
  showDeferred?: boolean;
  timerDisplay?: string;
  onNavigate?: (section: string, params?: any) => void;
  hideSearchButton?: boolean; // New prop to explicitly hide search button
  hideTimer?: boolean; // New prop to hide timer when coming from CA dashboard
  showNotificationBell?: boolean; // New prop to show notification bell
  onNotificationClick?: () => void; // New prop for notification click handler
  showAddSlotsButton?: boolean; // New prop to show Add Slots button
  onAddSlotsClick?: () => void; // New prop for Add Slots click handler
  showRefreshButton?: boolean; // New prop to show Refresh button for CA/PA users
  onRefreshClick?: () => void; // New prop for Refresh click handler
  isRefreshing?: boolean; // New prop to show loading state
}

const UnifiedHeader = ({
  title,
  showBackButton = false,
  onBackClick,
  showTimer = false,
  sessionTime = 0,
  isPaused = false,
  onToggleTimer,
  showQuickActions = false,
  onFollowUpClick,
  onDeferredClick,
  onSearchClick,
  userType,
  showFollowUp = false,
  showDeferred = false,
  timerDisplay,
  onNavigate,
  hideSearchButton = false, // New prop with default value
  hideTimer = false, // New prop with default value
  showNotificationBell = false, // New prop with default value
  onNotificationClick, // New prop for notification click handler
  showAddSlotsButton = false, // New prop with default value
  onAddSlotsClick, // New prop for Add Slots click handler
  showRefreshButton = false, // New prop with default value
  onRefreshClick, // New prop for Refresh click handler
  isRefreshing = false // New prop with default value
}: UnifiedHeaderProps) => {
  const { user, logout, hasuraToken, token } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { smsCount } = useSMSCount();
  const { voicemailCount } = useVoicemailCount();
  const { hboxCall } = useHBoxCall();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [engagementNumbers, setEngagementNumbers] = useState<any[]>([]);
  const [numberSources, setNumberSources] = useState<any[]>([]);
  const [allowedRoles, setAllowedRoles] = useState<string[]>([]);

  // Fetch enrollment numbers, number sources, and allowed roles on component mount
  useEffect(() => {
    const fetchDialerData = async () => {
      if (!hasuraToken) return;

      try {
        // Fetch allowed roles for outbound calling
        const roles = await clinicsGraphqlService.getOutboundCallRoles(hasuraToken);
        setAllowedRoles(roles);

        // Only fetch numbers if user has permission
        const currentUserType = userType || user?.type;
        if (currentUserType && roles.includes(currentUserType)) {
          const numbers = await clinicsGraphqlService.getAllEnrollmentNumbers(token);
          setEngagementNumbers(numbers.outboundNumber);

          const sources = await clinicsGraphqlService.getClinicNumberSourceTypes(hasuraToken);
          setNumberSources(sources);
        }
      } catch (error) {
        console.error('Error fetching dialer data:', error);
      }
    };

    fetchDialerData();
  }, [hasuraToken, userType, user?.type, token]);

  // Open outbound call dialer
  const openOutboundCall = () => {
    if (!hboxCall) {
      toast({
        title: 'Call System Not Ready',
        description: 'Please wait for the call system to initialize.',
        variant: 'destructive'
      });
      return;
    }

    if (engagementNumbers.length === 0) {
      toast({
        title: 'No Numbers Available',
        description: 'No enrollment numbers available for outbound calling.',
        variant: 'destructive'
      });
      return;
    }

    const modalOptions = {
      numbers: engagementNumbers?.length ? engagementNumbers?.map((number) => {return {phone_number: number.originNumber, number_source_id: number.numberSourceId}}) : [],
      numberSources: numberSources,
      callerId: user?.id,
      key: 'PEP'
    };

    console.log('modalOptions',modalOptions)

    hboxCall.showDialerModal(modalOptions);
  };

  // Timer logic now handled by GlobalTimelog component

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleLogout = async () => {
    await logout();
    setShowLogoutDialog(false);
    toast({
      title: "Signed Out",
      description: "You have been successfully signed out.",
      duration: 1000
    });
  };

  const handleNotificationClick = () => {
    navigate('/transfer-patients', {
      state: {
        from: 'ca-dashboard',
        returnTo: '/ca-dashboard'
      }
    });
  };

  const getUserTypeDisplay = () => {
    switch (userType || user?.type) {
      case 'VPE':
        return 'Virtual Patient Enroller (VPE)';
      case 'CS':
        return 'Care Specialist (CS)';
      case 'PE':
        return 'Patient Enrollment Specialist (PE)';
      case 'ES':
        return 'Enrollment Specialist (ES)';
      case 'PA':
        return 'Practice Administrator (PA)';
      case 'CA':
        return 'Care Administrator (CA)';
      case 'EA':
        return 'Enrollment Administrator (EA)';
      default:
        return title;
    }
  };

  return (
    <>
      {/* Main Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4">
            {showBackButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onBackClick}
                className="text-purple-600 hover:text-purple-900 hover:bg-purple-200"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
            <div className="flex items-center space-x-3">
              <div className="flex flex-col items-center">
                <img
                  src="/lovable-uploads/6823d161-b5be-421b-863a-12eaf2ea796e.png"
                  alt="HBox Logo"
                  className="h-10 w-auto"
                />
                <span className="text-xs text-black font-medium">{APP_VERSION}</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  PEP
                </h1>
                <p className="text-sm text-gray-600">{title !== 'PEP' ? title : getUserTypeDisplay()}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* 1. Search Patients Button - Hide when on global search page, patient details page, or when explicitly hidden */}
            {!hideSearchButton &&
              location.pathname !== '/global-search' &&
              !location.pathname.startsWith('/patient-details') &&
              title !== 'Global Patient Search' && (
                <Button
                  id="header_search_patients_btn"
                  onClick={() => navigate('/global-search')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium"
                >
                  <SearchIcon className="h-4 w-4 mr-2" />
                  Search Patients
                </Button>
              )}

            {/* Add Patient Button - Visible for all user roles */}
            <Button
              id="header_add_patient_btn"
              onClick={() => navigate('/add-patient')}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-4 py-2 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-300"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add Patient
            </Button>

            {/* 2. Notification Bell - Show when explicitly requested or for CA/CS users on main dashboard */}
            {showNotificationBell && (
              <NotificationBell onNotificationClick={onNotificationClick || handleNotificationClick} />
            )}
            {!showNotificationBell &&
              (['CA', 'CS'].includes(userType) || ['CA', 'CS'].includes(user?.type)) &&
              location.pathname === '/' &&
              title !== 'Global Patient Search' &&
              !location.pathname.startsWith('/patient-details') && (
                <NotificationBell onNotificationClick={onNotificationClick || handleNotificationClick} />
              )}

            {/* 3. Add Slots/CS Button/Add PES Button - Show when explicitly requested */}
            {showAddSlotsButton && (
              <Button
                onClick={onAddSlotsClick}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-2.5 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                <Plus className="h-4 w-4 mr-2" />
                 {userType === 'CA' ? 'Add CS' : userType === 'PA' && location.pathname === '/dashboard'? 'Add PES': 'Add Slots'}
              </Button>
            )}

            {/* 4. Refresh Button for CA/PA users - Show when explicitly requested */}
            {showRefreshButton && (['CA', 'PA'].includes(userType) || ['CA', 'PA'].includes(user?.type)) && (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={onRefreshClick}
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 rounded-full border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm"
                      disabled={isRefreshing}
                    >
                      <RefreshCw className={`h-4 w-4 text-gray-600 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-gray-900 text-white border-gray-800">
                    Refresh
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Conditional PE Buttons */}
            {showFollowUp && (
              <Button
                size="sm"
                onClick={() => {
                  if (onNavigate) {
                    onNavigate('followup-patients');
                  } else if (userType === 'PE') {
                    navigate('/pe-followup-patients');
                  } else {
                    onFollowUpClick?.();
                  }
                }}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Follow up Patients
              </Button>
            )}
            {showDeferred && (
              <Button
                size="sm"
                onClick={() => {
                  if (onNavigate) {
                    onNavigate('deferred-patients');
                  } else if (userType === 'PE') {
                    navigate('/pe-deferred-patients');
                  } else {
                    onDeferredClick?.();
                  }
                }}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Deferred Patients
              </Button>
            )}

            {/* Timer Components - Show for ES, CS, PE users (always show timer first) */}
            {!hideTimer && user?.type && !['PA', 'VPE'].includes(user.type) && (
              <>
                {/* Global Timelog with Controls - Show for ES, CS, PE users */}
                <GlobalTimelog />
              </>
            )}

            {/* Outbound Call Button - Show for users with permission */}
          {((userType && allowedRoles.includes(userType)) || (user?.type && allowedRoles.includes(user.type))) && (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    onClick={openOutboundCall}
                    variant="outline"
                    className="relative px-4 py-2 h-10 w-10 rounded-full font-medium"
                  >
                    <Phone />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-gray-900 text-white border-gray-800">
                  Make Outbound Call
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
            {/* Call history Link - Show for PA, CA, EA, ES, and VPE users only */}
          {location.pathname !== '/call-history' && ['EA', 'ES'].includes(userType || user?.type) && (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => navigate('/call-history')}
                    variant="outline"
                    className="relative px-4 py-2 h-10 w-10 rounded-full font-medium"
                  >
                    <Voicemail />
                    {voicemailCount > 0 && (
                      <Badge 
                        variant="destructive" 
                        className="absolute -top-2 -right-2 min-w-[20px] h-5 flex items-center justify-center px-1.5 py-0 text-xs font-bold bg-red-500 hover:bg-red-500"
                      >
                        {voicemailCount > 99 ? '99+' : voicemailCount}
                      </Badge>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-gray-900 text-white border-gray-800">
                  Call History
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

            {/* SMS Link - Show for PA, CA, EA, ES, and VPE users only */}
            {location.pathname !== '/sms' && ['PA', 'CA', 'EA', 'ES', 'VPE'].includes(userType || user?.type) && (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={() => navigate('/sms')}
                      variant="outline"
                      className="relative px-4 py-2 h-10 w-10 rounded-full font-medium"
                    >
                      <MessageSquareMore />
                      {smsCount > 0 && (
                        <Badge 
                          variant="destructive" 
                          className="absolute -top-2 -right-2 min-w-[20px] h-5 flex items-center justify-center px-1.5 py-0 text-xs font-bold bg-red-500 hover:bg-red-500"
                        >
                          {smsCount > 99 ? '99+' : smsCount}
                        </Badge>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-gray-900 text-white border-gray-800">
                    SMS
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Refresh Button - Show for ES, CS, PE users AFTER timer */}
            {showRefreshButton && (['ES', 'CS', 'PE'].includes(userType) || ['ES', 'CS', 'PE'].includes(user?.type)) && (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={onRefreshClick}
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 rounded-full border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm"
                      disabled={isRefreshing}
                    >
                      <RefreshCw className={`h-4 w-4 text-gray-600 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-gray-900 text-white border-gray-800">
                    Refresh
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* User Info */}
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <User className="h-4 w-4" />
              <span className="font-medium">{user?.fullName}</span>
            </div>

            {/* Sign Out Button */}
            <Button
              onClick={() => setShowLogoutDialog(true)}
              className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 py-2.5 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </header>




      {/* Logout Confirmation Dialog */}
      <LogoutConfirmDialog
        open={showLogoutDialog}
        onOpenChange={setShowLogoutDialog}
        onConfirm={handleLogout}
      />
    </>
  );
};

export default UnifiedHeader;
export type { UnifiedHeaderProps };